import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Copy, ShieldCheck, X, Ban } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

function relTime(iso) {
  if (!iso) return "—";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
}

function copy(text) {
  navigator.clipboard.writeText(text);
  toast.success("Link copied to clipboard");
}

function InviteModal({ open, onOpenChange, onCreate }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setEmail(""); setName(""); setResult(null); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admins/invite", { email, name });
      setResult(data);
      onCreate();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg bg-[#0A0A0A] border-zinc-800 rounded-sm text-zinc-100" data-testid="invite-modal">
        <DialogHeader>
          <DialogTitle className="font-mono tracking-tight">Invite New Admin</DialogTitle>
        </DialogHeader>

        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Name</Label>
              <Input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Email</Label>
              <Input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono" placeholder="jane@example.com" />
            </div>
            <p className="text-[11px] font-mono text-zinc-500 border-t border-zinc-800 pt-3">
              A one-time invitation link will be generated. Copy it and send it to the new admin so they can set their own password.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-sm border-zinc-800 bg-transparent" onClick={() => close(false)}>Cancel</Button>
              <Button data-testid="invite-generate-btn" type="submit" disabled={busy} className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono">
                {busy ? "GENERATING…" : "GENERATE INVITE"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-3 rounded-sm">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">Invitation ready</div>
              <div className="mt-1 text-sm font-mono">{result.name} · {result.email}</div>
              {result.email_status?.attempted && (
                <div className="mt-2 pt-2 border-t border-emerald-500/20 text-[11px] font-mono">
                  {result.email_status.ok ? (
                    <span className="text-emerald-400">✓ Invitation email sent to {result.email}</span>
                  ) : (
                    <span className="text-amber-400">⚠ Email send failed: {result.email_status.error} — use the link below manually</span>
                  )}
                </div>
              )}
              {!result.email_status?.attempted && (
                <div className="mt-2 pt-2 border-t border-emerald-500/20 text-[11px] font-mono text-zinc-500">
                  Email not configured — send the link below manually
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Invite link (valid 7 days)</Label>
              <div className="mt-1 flex gap-2">
                <Input data-testid="invite-link-input" readOnly value={result.invite_link} className="bg-transparent border-zinc-800 rounded-sm font-mono text-xs" />
                <Button data-testid="copy-invite-link-btn" type="button" onClick={() => copy(result.invite_link)} className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono gap-1">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <p className="mt-2 text-[11px] font-mono text-zinc-500">
                Send this link via WhatsApp, email or any channel. When opened, the invitee sets their password and gains admin access.
              </p>
            </div>
            <DialogFooter>
              <Button data-testid="close-invite-modal-btn" type="button" className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono" onClick={() => close(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }) {
  if (status === "pending") return <Badge className="rounded-sm bg-transparent border border-amber-500/60 text-amber-400 font-mono uppercase tracking-widest">PENDING</Badge>;
  if (status === "used") return <Badge className="rounded-sm bg-transparent border border-emerald-500/60 text-emerald-400 font-mono uppercase tracking-widest">USED</Badge>;
  if (status === "revoked") return <Badge className="rounded-sm bg-transparent border border-zinc-700 text-zinc-500 font-mono uppercase tracking-widest">REVOKED</Badge>;
  if (status === "expired") return <Badge className="rounded-sm bg-transparent border border-red-500/60 text-red-400 font-mono uppercase tracking-widest">EXPIRED</Badge>;
  return null;
}

export default function Admins() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const isSuper = user?.role === "super_admin";

  const { data: admins = [] } = useQuery({
    queryKey: ["admins-list"],
    queryFn: async () => (await api.get("/admins")).data,
  });
  const { data: invites = [] } = useQuery({
    queryKey: ["admins-invites"],
    queryFn: async () => (await api.get("/admins/invites")).data,
    enabled: isSuper,
    refetchInterval: 15000,
  });

  const delAdmin = useMutation({
    mutationFn: async (id) => api.delete(`/admins/${id}`),
    onSuccess: () => { toast.success("Admin removed"); qc.invalidateQueries({ queryKey: ["admins-list"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const revokeInvite = useMutation({
    mutationFn: async (id) => api.delete(`/admins/invites/${id}`),
    onSuccess: () => { toast.success("Invitation revoked"); qc.invalidateQueries({ queryKey: ["admins-invites"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div className="space-y-6" data-testid="admins-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Access Control</div>
          <h1 className="text-2xl md:text-3xl font-mono font-semibold tracking-tight">Admin Management</h1>
          <p className="text-sm text-zinc-400 mt-1">
            {isSuper
              ? "Super admin panel — invite, list and remove admins."
              : "Read-only view — only the super admin can invite or remove admins."}
          </p>
        </div>
        {isSuper && (
          <Button data-testid="open-invite-btn" onClick={() => setInviteOpen(true)} className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono gap-2">
            <UserPlus className="h-4 w-4" /> Invite Admin
          </Button>
        )}
      </div>

      {/* Current admins */}
      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm">
        <div className="px-5 py-3 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Active accounts</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-5 py-2">Name</th>
                <th className="text-left px-5 py-2">Email</th>
                <th className="text-left px-5 py-2">Role</th>
                <th className="text-left px-5 py-2">Created</th>
                <th className="text-right px-5 py-2">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="admins-tbody">
              {admins.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-6 text-center text-zinc-500 font-mono text-xs">— no admins —</td></tr>
              )}
              {admins.map((a) => (
                <tr key={a.id} className="border-b border-zinc-900 hover:bg-zinc-900/60" data-testid={`admin-row-${a.id}`}>
                  <td className="px-5 py-2 font-mono flex items-center gap-2">
                    {a.is_super_admin && <ShieldCheck className="h-4 w-4 text-emerald-400" title="Super admin (immutable)" />}
                    {a.name || "—"}
                  </td>
                  <td className="px-5 py-2 font-mono text-zinc-300 truncate max-w-[240px]" title={a.email}>{a.email}</td>
                  <td className="px-5 py-2 font-mono">
                    {a.is_super_admin ? (
                      <Badge className="rounded-sm bg-transparent border border-emerald-500/60 text-emerald-400 font-mono uppercase tracking-widest">SUPER ADMIN</Badge>
                    ) : (
                      <Badge className="rounded-sm bg-transparent border border-zinc-700 text-zinc-300 font-mono uppercase tracking-widest">ADMIN</Badge>
                    )}
                  </td>
                  <td className="px-5 py-2 font-mono text-xs text-zinc-400">{relTime(a.created_at)}</td>
                  <td className="px-5 py-2 text-right">
                    {isSuper && !a.is_super_admin && a.id !== user?.id && (
                      <Button
                        data-testid={`delete-admin-btn-${a.id}`}
                        size="sm"
                        variant="outline"
                        className="h-8 rounded-sm border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10"
                        onClick={() => { if (confirm(`Remove admin "${a.email}"?`)) delAdmin.mutate(a.id); }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {a.is_super_admin && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                        <ShieldCheck className="h-3 w-3" /> immutable
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invitations (super admin only) */}
      {isSuper && (
        <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm">
          <div className="px-5 py-3 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Invitations</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                  <th className="text-left px-5 py-2">Invitee</th>
                  <th className="text-left px-5 py-2">Status</th>
                  <th className="text-left px-5 py-2">Sent</th>
                  <th className="text-left px-5 py-2">Expires</th>
                  <th className="text-right px-5 py-2">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="invites-tbody">
                {invites.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-6 text-center text-zinc-500 font-mono text-xs">— no invitations sent yet —</td></tr>
                )}
                {invites.map((inv) => (
                  <tr key={inv.id} className="border-b border-zinc-900 hover:bg-zinc-900/60">
                    <td className="px-5 py-2 font-mono">
                      <div className="text-white">{inv.name}</div>
                      <div className="text-[10px] text-zinc-400">{inv.email}</div>
                    </td>
                    <td className="px-5 py-2"><StatusBadge status={inv.status} /></td>
                    <td className="px-5 py-2 font-mono text-xs text-zinc-400">{relTime(inv.created_at)}</td>
                    <td className="px-5 py-2 font-mono text-xs text-zinc-400">{relTime(inv.expires_at)}</td>
                    <td className="px-5 py-2 text-right">
                      <div className="inline-flex gap-1">
                        {inv.status === "pending" && (
                          <>
                            <Button
                              data-testid={`copy-link-btn-${inv.id}`}
                              size="sm"
                              variant="outline"
                              className="h-8 rounded-sm border-zinc-800 bg-transparent"
                              onClick={() => copy(inv.invite_link)}
                              title="Copy invite link"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              data-testid={`revoke-invite-btn-${inv.id}`}
                              size="sm"
                              variant="outline"
                              className="h-8 rounded-sm border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10"
                              onClick={() => { if (confirm(`Revoke invitation for ${inv.email}?`)) revokeInvite.mutate(inv.id); }}
                              title="Revoke"
                            >
                              <Ban className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <InviteModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onCreate={() => qc.invalidateQueries({ queryKey: ["admins-invites"] })}
      />
    </div>
  );
}
